"use client";

import { Clock, FileText, HelpCircle } from "lucide-react";
import IconDetail from "./IconDetail";

interface FormMetricsProps {
    timeToComplete?: number | null;
    numberOfPages?: number | null;
    numberOfQuestions?: number | null;
    className?: string;
    variant?: "horizontal" | "vertical";
    size?: "sm" | "md" | "lg";
}

export function FormMetrics({
    timeToComplete,
    numberOfPages,
    numberOfQuestions,
    className = "",
    variant = "horizontal",
    size = "md",
}: FormMetricsProps) {
    const containerClass = variant === "horizontal" ? "flex flex-wrap items-center gap-4" : "flex flex-col gap-3";

    const sizeClasses = {
        sm: "text-sm",
        md: "text-base",
        lg: "text-lg",
    };

    const metrics = [
        {
            show: timeToComplete !== null && timeToComplete !== undefined,
            icon: <Clock className="text-cyan-600 dark:text-cyan-400" />,
            label: "Time",
            value: timeToComplete ? `${timeToComplete} min` : null,
            anno: "time",
        },
        {
            show: numberOfPages !== null && numberOfPages !== undefined,
            icon: <FileText className="text-indigo-600 dark:text-indigo-400" />,
            label: "Pages",
            value: numberOfPages,
            anno: "pages",
        },
        {
            show: numberOfQuestions !== null && numberOfQuestions !== undefined,
            icon: <HelpCircle className="text-purple-600 dark:text-purple-400" />,
            label: "Questions",
            value: numberOfQuestions,
            anno: "questions",
        },
    ];

    const visibleMetrics = metrics.filter((m) => m.show);

    if (visibleMetrics.length === 0) return null;

    return (
        <div className={`${containerClass} ${sizeClasses[size]} ${className}`}>
            {visibleMetrics.map((metric) => (
                <div key={metric.anno} data-anno={metric.anno} className="flex items-center gap-2">
                    {variant === "horizontal" && (
                        <IconDetail
                            icon={metric.icon}
                            words={
                                <>
                                    <span className="pr-2 font-semibold text-slate-800 dark:text-slate-200">
                                        {metric.label}:
                                    </span>
                                    <span className="text-slate-700 dark:text-slate-300">{metric.value}</span>
                                </>
                            }
                        />
                    )}
                    {variant === "vertical" && (
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2 mb-1">
                                {metric.icon}
                                <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                                    {metric.label}
                                </span>
                            </div>
                            <span className="ml-7 pl-2 text-slate-700 dark:text-slate-300">{metric.value}</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
